import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import '@/shared/features/classroom/constants/events.quiz-classroom-point';
import { useTrackEvent } from '@zep/logger/react';
import { useLocale } from '@/hooks';
import { PromotionModal } from '@/shared/components/PromotionModal';
import { CLASSROOM_ROUTES } from '@/shared/constants/routes';
import { openClassroomTab } from '../utils';

/**
 * 포인트 출시 안내 모달 카피. defaultValue 는 S3 locale 캐시 갱신 전까지의 안전한 폴백이다.
 * (설명 모달 POINT_INTRO_COPY 와 별개 — 이쪽은 로드 시 자동으로 뜨는 '기능 나왔어요' 알림)
 */
export const POINT_RELEASE_COPY = {
  title: {
    key: 'common:pointRelease.title'
  },
  description: {
    key: 'common:pointRelease.description'
  },
  primaryButton: {
    key: 'common:pointRelease.primaryButton'
  }
} as const;

// 젤리(Zelly) 전용 일러스트 — 로케일 텍스트가 없어 단일 이미지로 공용.
// 포인트(젤리) "더 알아보기" 가이드 — 로케일별 채널톡 아티클. 그 외 로케일은 en 폴백.
const GUIDE_LINKS = {
  ko: 'https://docs.channel.io/zepquiz/ko/articles/0aaff2e5',
  en: 'https://docs.channel.io/zepquiz/en/articles/0aaff2e5',
  ja: 'https://docs.channel.io/zepquiz/ja/articles/0aaff2e5'
} as const;
interface PointReleaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  triggeredOn?: string;
}

/**
 * 포인트 출시 안내 모달.
 * 앱 진입 시 선생님(학생 외 계정)에게 계정당 1회 자동 노출한다.
 * (실제 노출 판정은 usePointReleaseModal 이 담당)
 */
export const PointReleaseModal = ({
  isOpen,
  onClose,
  triggeredOn
}: PointReleaseModalProps) => {
  const {
    t
  } = useTranslation(['common']);
  const {
    locale
  } = useLocale();
  const router = useRouter();
  const hasTrackedView = useRef(false);
  const {
    track
  } = useTrackEvent();
  const language = locale.split('-')[0];
  const guideLink = GUIDE_LINKS[language as keyof typeof GUIDE_LINKS] ?? GUIDE_LINKS.en;
  useEffect(() => {
    if (isOpen && !hasTrackedView.current) {
      track('quiz_point_release_view', {
        place: triggeredOn ?? ''
      });
      hasTrackedView.current = true;
    }
  }, [isOpen, triggeredOn, track]);
  const handlePrimaryClick = () => {
    track('quiz_point_release_start_click', {});
    onClose();
    // '시작하기' → 내 클래스 리스트로 보낸다(자료실 등에서 떠도 클래스로 진입).
    void router.push(CLASSROOM_ROUTES.list());
  };
  const handleSecondaryClick = () => {
    track('quiz_point_guide_click', {
      place: 'release'
    });
    openClassroomTab(guideLink);
  };
  return <PromotionModal isOpen={isOpen} onClose={onClose} title={t(POINT_RELEASE_COPY.title.key)} description={t(POINT_RELEASE_COPY.description.key)} imageUrl="/assets/point-release-modal-zelly@2x.webp" badge={{
    label: 'New',
    className: 'bg-blue-50 border-opacity-assistive',
    textClassName: 'text-blue-500'
  }} primaryLabel={t(POINT_RELEASE_COPY.primaryButton.key)} primaryClassName="hover-shine bg-gradient-to-r from-blue-400 to-mint-600" onPrimaryClick={handlePrimaryClick}
  // '더 알아보기' 는 클래스 출시 모달과 동일 카피(시트 등록 키 재사용).
  secondaryLabel={t('common:classroomPromotion.secondaryButton')} onSecondaryClick={handleSecondaryClick} data-sentry-element="PromotionModal" data-sentry-component="PointReleaseModal" data-sentry-source-file="PointReleaseModal.tsx" />;
};